import { useEffect, useRef, useState } from 'react'
import { Reorder } from 'framer-motion'
import { GripVertical, MoreHorizontal, Star } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { formatPhoneDisplay } from '@/lib/phoneValidation'
import { nomLigne, useSaveLineOrder, type InboxLineSummary } from '@/api/inbox'
import { useAvailableCallerIds } from '@/api/agents'
import { useProfile, useUpdateProfile } from '@/api/profile'
import { saveSessionCallerId } from '@/lib/sessionCallerId'

interface Props {
  lignes: InboxLineSummary[]
  /** La Boîte de réception est l'écran affiché. */
  active: boolean
  lineFilter: string | null
  unreadTotal: number
  onChoose: (ligne: string | null) => void
  darkMode: boolean
}

/**
 * Les lignes sous « Boîte de réception » : « Toutes », puis chaque ligne dans
 * l'ordre de l'utilisateur (à glisser), avec ses non lus. Le menu « ⋯ » d'une
 * ligne en fait la ligne par défaut des appels et textos sortants.
 */
export function LignesBoite({ lignes, active, lineFilter, unreadTotal, onChoose, darkMode }: Props) {
  const { t } = useTranslation()
  const sauverOrdre = useSaveLineOrder()
  const { data: profile } = useProfile()
  const updateProfile = useUpdateProfile()
  const { data: sortants = [] } = useAvailableCallerIds()
  const parDefaut = profile?.preferred_caller_id || ''
  const [menu, setMenu] = useState<string | null>(null)
  // L'ordre affiché, suivi pendant le glisser ; le serveur ne reçoit que
  // l'ordre final, au lâcher.
  const [ordre, setOrdre] = useState(() => lignes.map((l) => l.number))
  const ordreRef = useRef(ordre)
  const enGlisse = useRef(false)
  const vientDeGlisser = useRef(false)
  const cleLignes = lignes.map((l) => l.number).join(',')
  useEffect(() => {
    if (enGlisse.current) return
    const suivant = cleLignes.split(',').filter(Boolean)
    ordreRef.current = suivant
    setOrdre(suivant)
  }, [cleLignes])
  const parNumero = new Map(lignes.map((l) => [l.number, l]))
  // La ligne glissée reste dans la liste des lignes (elle ne passe pas sur « Toutes »).
  const groupeRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menu) return
    const fermer = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenu(null)
    }
    document.addEventListener('mousedown', fermer)
    return () => document.removeEventListener('mousedown', fermer)
  }, [menu])

  const reordonner = (suivant: string[]) => {
    ordreRef.current = suivant
    setOrdre(suivant)
  }

  const lacher = () => {
    enGlisse.current = false
    // Le clic qui suit un glisser ne doit pas ouvrir la ligne.
    vientDeGlisser.current = true
    setTimeout(() => (vientDeGlisser.current = false), 150)
    if (ordreRef.current.join(',') !== cleLignes) sauverOrdre.mutate(ordreRef.current)
  }

  const utiliserParDefaut = (l: InboxLineSummary) => {
    setMenu(null)
    updateProfile.mutate(
      { preferred_caller_id: l.number },
      {
        onSuccess: () => {
          // Appliquée tout de suite, sans attendre la prochaine session.
          try {
            saveSessionCallerId(l.number)
          } catch {
            /* stockage indisponible : la préférence s'appliquera au prochain démarrage */
          }
          toast.success(t('inbox.defaultLineSet', 'Les appels et textos partiront de {{line}}.', { line: nomLigne(l, formatPhoneDisplay) }))
        },
        onError: () => toast.error(t('phone.callerId.preferredError', 'Impossible de sauvegarder')),
      },
    )
  }

  const entree = (actif: boolean) =>
    cn(
      'group w-full flex items-center gap-1.5 rounded-lg pl-1.5 pr-2 py-1.5 text-[12.5px] transition-colors',
      actif
        ? darkMode
          ? 'bg-slate-800 text-white font-medium'
          : 'bg-slate-100 text-slate-900 font-medium'
        : darkMode
          ? 'text-slate-400 hover:bg-slate-800/60 hover:text-white'
          : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900',
    )
  const pastille = (n: number) =>
    n > 0 && (
      <span className={cn('text-[10.5px] font-semibold tabular-nums', darkMode ? 'text-indigo-300' : 'text-indigo-600')}>{n}</span>
    )

  return (
    <div className="ml-6 mt-0.5 mb-1 space-y-0.5">
      <button type="button" onClick={() => onChoose(null)} className={cn(entree(active && lineFilter === null), 'text-left')}>
        <span className="w-3.5 flex-shrink-0" />
        <span className="flex-1 min-w-0 truncate text-left">{t('inbox.allLines', 'Toutes')}</span>
        {pastille(unreadTotal)}
      </button>

      <Reorder.Group ref={groupeRef} as="div" axis="y" values={ordre} onReorder={reordonner} className="space-y-0.5">
      {ordre.map((numero) => {
        const l = parNumero.get(numero)
        if (!l) return null
        const sortante = sortants.some((n) => n.phone_number === l.number)
        return (
          <Reorder.Item
            as="div"
            key={l.number}
            value={l.number}
            className="relative"
            onDragStart={() => {
              enGlisse.current = true
              setMenu(null)
            }}
            onDragEnd={lacher}
            dragConstraints={groupeRef}
            dragElastic={0.08}
            style={{ borderRadius: 8 }}
            whileDrag={{
              scale: 1.03,
              zIndex: 20,
              backgroundColor: darkMode ? '#1e293b' : '#ffffff',
              boxShadow: '0 8px 20px rgba(15, 23, 42, 0.14)',
            }}
            transition={{ type: 'spring', stiffness: 520, damping: 40 }}
          >
            <div className={cn(entree(active && lineFilter === l.number), 'relative cursor-grab active:cursor-grabbing')}>
              <button
                type="button"
                onClick={() => !vientDeGlisser.current && onChoose(l.number)}
                className="flex-1 min-w-0 flex items-center gap-1.5 text-left select-none"
                title={formatPhoneDisplay(l.number)}
              >
                <GripVertical className="h-3.5 w-3.5 flex-shrink-0 cursor-grab opacity-0 group-hover:opacity-50" />
                <span className="flex-1 min-w-0 truncate">{nomLigne(l, formatPhoneDisplay)}</span>
                {parDefaut === l.number && (
                  <Star
                    className="h-3 w-3 flex-shrink-0 fill-amber-400 text-amber-400"
                    aria-label={t('inbox.defaultLine', 'Ligne par défaut')}
                  />
                )}
                {pastille(l.unread_threads)}
              </button>
              {sortante && (
                <button
                  type="button"
                  onClick={() => setMenu(menu === l.number ? null : l.number)}
                  aria-label={t('inbox.lineMenu', 'Options de la ligne')}
                  className={cn(
                    // Par-dessus la fin de la ligne, au survol : le nom garde toute la largeur.
                    'absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 opacity-0 group-hover:opacity-100 focus:opacity-100',
                    menu === l.number && 'opacity-100',
                    darkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-slate-100 hover:bg-slate-200',
                  )}
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {menu === l.number && (
              <div
                ref={menuRef}
                className={cn(
                  'absolute right-0 top-full z-30 mt-1 w-56 rounded-lg border p-1 shadow-lg',
                  darkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white',
                )}
              >
                <button
                  type="button"
                  disabled={parDefaut === l.number}
                  onClick={() => utiliserParDefaut(l)}
                  className={cn(
                    'w-full flex items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[12.5px]',
                    darkMode ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50',
                    'disabled:opacity-60 disabled:hover:bg-transparent',
                  )}
                >
                  <Star className={cn('h-3.5 w-3.5', parDefaut === l.number && 'fill-amber-400 text-amber-400')} />
                  {parDefaut === l.number
                    ? t('inbox.isDefaultLine', 'Ligne par défaut')
                    : t('inbox.useAsDefault', 'Utiliser par défaut')}
                </button>
              </div>
            )}
          </Reorder.Item>
        )
      })}
      </Reorder.Group>
    </div>
  )
}
